import React from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import linkIcon from '../../asset/icon/link.png';
import linkCutIcon from '../../asset/icon/linkCut.png';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { getHintMessage } from '../../shared/constants/hintMessages';
import type { MemberSearchData, WorkArtistData } from '../../shared/dto/EntityRepository';
import { ModalType, TextAlignment, WorkContentMode } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { EMPTY_WORK_ARTIST } from '../../shared/util/workArtist';

interface WorkArtistEditorProps {
  artist: WorkArtistData | null | undefined;
  onChange: (artist: WorkArtistData | null) => void;
  mixed?: boolean;
  workContentMode?: WorkContentMode;
  showAccountAction?: boolean;
}

const WorkArtistEditor: React.FC<WorkArtistEditorProps> = ({
  artist,
  onChange,
  mixed = false,
  workContentMode,
  showAccountAction = true,
}) => {
  const current = artist ?? EMPTY_WORK_ARTIST;
  const { setStandardModal } = useModalStore();

  const patchArtist = (updates: Partial<WorkArtistData>) => {
    onChange({ ...current, ...updates });
  };

  const handleSelectMember = (member: MemberSearchData) => {
    onChange({
      name: member.name?.trim() || member.username,
      nameEn: member.nameEn?.trim() || '',
      linkedMemberAui: member.aui,
      linkedMemberHandle: member.handle,
    });
  };

  const openArtistSearchModal = () => {
    setStandardModal({
      modalType: ModalType.WORK_ARTIST_SEARCH,
      title: null,
      value: null,
      handleChange: (_value, payload) => {
        if (!payload) return;
        handleSelectMember(payload as MemberSearchData);
      },
    });
  };

  const isLinked = Boolean(current.linkedMemberAui);
  const artistNameFields = workContentMode == null
    ? [
        { field: 'name' as const, label: 'Artist', placeholder: 'Artist name' },
        { field: 'nameEn' as const, label: 'Artist (en)', placeholder: 'Artist name (en)' },
      ]
    : workContentMode === WorkContentMode.ENG
      ? [{ field: 'nameEn' as const, label: 'Artist (en)', placeholder: 'Artist name (en)' }]
      : [{ field: 'name' as const, label: 'Artist', placeholder: 'Artist name' }];

  const changeArtistName = (field: 'name' | 'nameEn', value: string) => {
    if (field === 'name') {
      patchArtist({ name: value });
      return;
    }
    patchArtist({ nameEn: value });
  };

  return (
    <EditorContainer onClick={(event) => event.stopPropagation()}>
      {artistNameFields.map(({ field, label, placeholder }, index) => (
        <FieldRow key={field}>
          <FieldLabel>{label}</FieldLabel>
          <FieldContent>
            <HeadlessInput
              type="text"
              value={current[field] ?? ''}
              placeholder={mixed && !artist ? 'Mixed' : placeholder}
              handleChange={(event) => changeArtistName(field, event.target.value)}
              StyledInput={ArtistInput}
              $alignment={TextAlignment.LEFT}
            />
            {showAccountAction && index === 0 && (
              <HintSmallWrapper
                content={getHintMessage(isLinked ? 'DISCONNECT_ACCOUNT' : 'CONNECT_ACCOUNT')}
                toBottom={true}
                toLeft={true}
              >
                {isLinked ? (
                  <UniversalIconBtn
                    size="small"
                    border={false}
                    isTransparent
                    backgroundImg={linkCutIcon}
                    onClick={() => patchArtist({ linkedMemberAui: null, linkedMemberHandle: null })}
                    aria-label="Disconnect account"
                  />
                ) : (
                  <UniversalIconBtn
                    size="small"
                    border={false}
                    isTransparent
                    backgroundImg={linkIcon}
                    onClick={openArtistSearchModal}
                    aria-label="Connect account"
                  />
                )}
              </HintSmallWrapper>
            )}
          </FieldContent>
        </FieldRow>
      ))}
    </EditorContainer>
  );
};

export default WorkArtistEditor;

const EditorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldRow = styled.div`
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldLabel = styled.span`
  width: 70px;
  min-width: 70px;
  padding-top: 2px;
  font-weight: 500;
`;

const FieldContent = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ArtistInput = styled.input.attrs({ maxLength: 255 })`
  width: auto;
  min-width: 0;
  flex: 1;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  outline: none;
  ${({ theme }) => theme.typography.Body_04};
`;
